import { useEffect, useRef, useState } from 'react';

import gql from 'graphql-tag';

import { ApolloError } from '@apollo/client';

import { getClient } from 'lane-shared/apollo';
import { PublicUserFragment } from 'lane-shared/graphql/fragments';
import { useMultiLanguage } from 'lane-shared/hooks/useMultiLanguage';
import useFlag from 'lane-shared/hooks/useFlag';
import { FeatureFlag } from 'constants-flags';
import { useSignedUrlRefresh } from './useSignedUrlRefresh';
import { ContentForCardType } from 'application-types';

export const ContentForCardFragment = `
  fragment ContentForCardFragment on Content {
    _id
    _created
    _updated
    name
    name_l10n
    description
    description_l10n
    subtitle
    slug
    type
    tags
    color
    isInteractive
    backgroundImage
    channel {
      _id
      settings {
        multiLanguageEnabled
        channelLanguages
        enabledLanguagesForMembers
      }
    }
    icon
    iconSet
    iconWeight
    backgroundColor
    liveDate
    startDate
    endDate
    unpublishDate
    interactionEndDate
    version
    renderer
    externalUrl {
      _id
      mediaId
      url
      openingMode
    }
    signedExternalUrl
    deepLink {
      _id
      deepLinkUrl
      appStoreUrl
      googlePlayStoreUrl
    }
    card {
      _id
    }

    integration {
      _id
      settings
      integration {
        _id
        name
        settings
      }
    }

    features {
      _id
      type
      feature
    }

    publishedBy {
      ...PublicUserFragment
    }

    channel {
      _id
    }
  }
`;

const getContentForCardQuery = gql`
  ${PublicUserFragment}
  ${ContentForCardFragment}

  query getContentForCardQuery($contentId: UUID!) {
    content(_id: $contentId) {
      ...ContentForCardFragment
    }
  }
`;

export function useContentForCardQuery({
  contentId,
}: {
  contentId: string | null | undefined;
}): {
  content: ContentForCardType | null;
  loading: boolean;
  error: ApolloError | null | undefined;
  fetchContent: (cId: string) => void;
} {
  const { translate } = useMultiLanguage();
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<any>(undefined);
  const [error, setError] = useState<any>(undefined);
  const isMounted = useRef(false);
  const isCacheFlagEnabled = useFlag<boolean>(
    FeatureFlag.ContentUsesCache,
    false
  );

  const isSignedUrlRefreshEnabled = useFlag<boolean>(
    FeatureFlag.AceContentForCardsSignedUrlRefresh,
    false
  );

  const { refreshIfNeeded, cleanup } = useSignedUrlRefresh({
    onUrlRefreshed: (mediaId: string, newUrl: string) => {
      // Update the content state with the new URL
      setContent((prevContent: ContentForCardType) => {
        if (prevContent && prevContent?.signedExternalUrl) {
          return {
            ...prevContent,
            signedExternalUrl: newUrl,
          };
        }

        return prevContent;
      });
    },
  });

  async function refreshSignedExternalUrl(data: {
    content: ContentForCardType;
  }) {
    try {
      if (
        data.content?.signedExternalUrl &&
        data.content?.externalUrl?.mediaId
      ) {
        const freshUrl = await refreshIfNeeded(
          data.content.externalUrl.mediaId,
          data.content.signedExternalUrl
        );

        const updatedData = {
          ...data,
          content: { ...data.content, signedExternalUrl: freshUrl },
        };

        return updatedData;
      }
    } catch (error) {
      console.error('Error in refreshSignedExternalUrl:', error);
    }

    return data;
  }

  async function fetchContent(cId: any) {
    setLoading(true);

    if (cId) {
      try {
        const { data } = await getClient().query({
          query: getContentForCardQuery,
          variables: { contentId: cId },
          fetchPolicy: isCacheFlagEnabled ? 'cache-first' : 'network-only',
        });

        // in case unmounting happens while the data is being retrieved:
        if (isMounted.current) {
          const updatedData = isSignedUrlRefreshEnabled
            ? await refreshSignedExternalUrl(data)
            : data;

          setContent(updatedData.content);
          setError(undefined);
        }
      } catch (err) {
        setContent(undefined);
        setError(err);
      }
    } else {
      setContent(undefined);
    }

    setLoading(false);
  }

  // set isMounted to true on mounting, and do cleanup once unmounted
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      cleanup();
    };
  }, [cleanup]);

  useEffect(() => {
    fetchContent(contentId);
  }, [contentId]);

  return {
    content: translate({
      model: content,
      columns: ['name', 'description', 'subtitle'],
    }),
    loading,
    error,
    fetchContent,
  };
}
